/**
 * DecorationsSelectionPopup — native decorations picker (lifted from
 * `common/StagingFlow/RoomItems/DecorationsSelectionPopup.component.tsx`, rewired
 * onto the module working context + `patchRoom` + plain-JS `decorations` array).
 * Decorations catalogue via `useStagingDecorations` (`components/contexts/*` —
 * ALLOWED). Analytics dropped (DF-staging-analytics).
 */

import { FC, useMemo } from 'react'

import { StagingItemSelectionPopup, StagingItemSelectionTab } from 'components/common/StagingFlow/StagingItemSelectionPopup'
import type { StagingDecorationDTO } from 'models/virtualFurniture'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import { uniqBy } from 'lodash'
import { useStagingDecorations } from 'components/contexts/StagingDecorations.context'
import { useTranslation } from 'react-i18next'

import { useStagingWorkingState } from '../StagingWorkingState.context'

export const DecorationsSelectionPopup: FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const { t } = useTranslation(['style_selection_order_step', 'staging_decorations'])
  const { stagingDecorationsByRoom } = useStagingDecorations()
  const { activeRoom, setRoomItemPopupType, patchRoom } = useStagingWorkingState()

  const decorationValuesForToggle: ToggleFilterValue<string>[] = useMemo(() => {
    if (!activeRoom || activeRoom.roomTypes.length === 0) return []

    const currentDecorations: StagingDecorationDTO[] = []
    for (const roomType of activeRoom.roomTypes) {
      currentDecorations.push(...(stagingDecorationsByRoom[roomType] || []))
    }

    return uniqBy(currentDecorations, 'item').map((decoration) => ({
      value: decoration.item,
      displayName: t(`staging_decorations:${decoration.item}`),
    }))
  }, [activeRoom, stagingDecorationsByRoom, t])

  if (!activeRoom) return null

  return (
    <StagingItemSelectionPopup
      title={t('style_selection_order_step:room_items.DECORATION')}
      isOpen={isOpen}
      availableButtonItems={{ [StagingItemSelectionTab.ALL]: decorationValuesForToggle }}
      onClose={() => setRoomItemPopupType(null)}
      selectedItemsMulti={{ [StagingItemSelectionTab.ALL]: new Set(activeRoom.decorations) }}
      onSelectMulti={(selectedStyles) => {
        patchRoom(activeRoom.key, { decorations: Array.from(selectedStyles[StagingItemSelectionTab.ALL]) })
        setRoomItemPopupType(null)
      }}
    />
  )
}
